import { renderGrid, renderXAxisLabels, renderYaxisNumerical } from '../../zoompan-canvas/renderAxes';
import {
  getBoundData,
  getDendrogramBinding,
  isDendrogramBinding,
  TpBoundData,
  TpChartConfigParams,
  TpChartDataBinding,
} from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { getDendrogram } from '../../../../data/analyticsDataSlice';
import { TpChartManifest } from '../interfaces';
import {
  chartDims,
  convertColumnDataListToDisplayString,
  convertColumnDataToDisplayString,
  createSortIndexes,
  generateChartTitle,
  getSortIndex,
  isSelectedItem,
} from '../../helpers/misc';
import { currentTheme, getThemedGrayFraction } from '../../../themes/themes';
import { Color, color, getDiscreteColorSet } from '../../helpers/colors';
import { LEGEND_ITEM_TYPES, TpLegendItem, renderLegendItems, renderYLabel } from '../../helpers/renderLegend';
import { getUserError } from '../../../../components/miscUtils';
import { TpDashboardGlobalChartConfig, TpGlobalChartColorMappingConfig } from '../../dashboard/interfaces';
import { getCategoricalCustomGlobalConfigOrder, getCategoricalCustomConfigOrder } from '../../helpers/colorMappingInfo';

interface TpCompositionGroupInfo {
  groupId: any;
  isOtherGroup: boolean;
  groupName: string;
  totalAbundance?: number;
  color: Color;
}

function calculateCompositionGroups(boundData: TpBoundData, config: TpChartConfigParams) {
  const matrixColumns = boundData.matrixColumnInfo!.matrixColumns;
  const rowCompositionGroupData = boundData.bindings.compositionGroups.data;
  const selectedRowItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;
  const dataRowItemId = boundData.bindings.itemId.data;
  const maxGroupCount = config.maxGroupCount;
  const rowCount = rowCompositionGroupData.length; // note: rows = OTUs
  const plotRelative = config.stackingType == 'relative';

  // Calculate which are the most abundant groups over all samples, as we may need to truncate that list
  const compositionGroupAbundancesMap: { [id: string]: number } = {};
  for (let colNr = 0; colNr < matrixColumns.length; colNr++) {
    // Note: columns = samples
    const column = matrixColumns[colNr];
    const columnData = boundData.matrixColumnInfo!.matrixColumnsData[column.id];
    for (let rowNr = 0; rowNr < rowCount; rowNr++) {
      // Note: rows = OTUs
      const groupId = rowCompositionGroupData[rowNr];
      if (!(groupId in compositionGroupAbundancesMap)) compositionGroupAbundancesMap[groupId] = 0;
      compositionGroupAbundancesMap[groupId] += columnData[rowNr];
    }
  }
  const compositionGroupsOrig: TpCompositionGroupInfo[] = []; // full, untruncated set
  for (const groupId of Object.keys(compositionGroupAbundancesMap))
    compositionGroupsOrig.push({
      groupId,
      isOtherGroup: false,
      groupName: convertColumnDataToDisplayString(boundData.bindings.compositionGroups.columnInfo, groupId),
      totalAbundance: compositionGroupAbundancesMap[groupId],
      color: color(0, 0, 0), //placeholder for color, actual rendering color is defined elsewhere
    });
  compositionGroupsOrig.sort((grp1, grp2) => grp2.totalAbundance! - grp1.totalAbundance!);

  // Create the truncated set (in case it is truncated)
  const isTruncated = compositionGroupsOrig.length > maxGroupCount;
  const compositionGroups = isTruncated ? compositionGroupsOrig.slice(0, maxGroupCount) : compositionGroupsOrig;

  // Create lookup map, connecting groupIds in the data to group indices
  const groupId2GroupIndex: { [id: string]: number } = {};
  for (const [index, group] of compositionGroups.entries()) groupId2GroupIndex[group.groupId] = index;

  // In case it's truncated, we add one extra "other" group for all the rest
  if (isTruncated) {
    compositionGroups.push({
      groupId: null,
      isOtherGroup: true,
      groupName: 'Other',
      color: color(0, 0, 0),
    });
  }
  // All groups that get truncated are assigned to the "other" group
  for (const group of compositionGroupsOrig)
    if (!(group.groupId in groupId2GroupIndex)) groupId2GroupIndex[group.groupId] = maxGroupCount;

  // Calculate group abundances per column (= sample)
  const columnsGroupAbundances: number[][] = [];
  const columnsGroupAbundancesSelected: number[][] = [];
  const columnTotalAbundances: number[] = [];
  let maxColumnTotalAbundance = 0;
  for (let colNr = 0; colNr < matrixColumns.length; colNr++) {
    const column = matrixColumns[colNr];
    const singleColumnGroupAbundances = Array(compositionGroups.length).fill(0);
    const singleColumnGroupAbundancesSelected = Array(compositionGroups.length).fill(0);
    const columnData = boundData.matrixColumnInfo!.matrixColumnsData[column.id];
    for (let rowNr = 0; rowNr < rowCount; rowNr++) {
      const groupId = rowCompositionGroupData[rowNr];
      const abundance = columnData[rowNr];
      singleColumnGroupAbundances[groupId2GroupIndex[groupId]] += abundance;
      if (isSelectedItem(selectedRowItemsMap, dataRowItemId[rowNr]))
        singleColumnGroupAbundancesSelected[groupId2GroupIndex[groupId]] += abundance;
    }
    columnsGroupAbundances.push(singleColumnGroupAbundances);
    columnsGroupAbundancesSelected.push(singleColumnGroupAbundancesSelected);
    const totAbundance = singleColumnGroupAbundances.reduce((acc, val) => acc + val, 0);
    columnTotalAbundances.push(totAbundance);
    maxColumnTotalAbundance = Math.max(maxColumnTotalAbundance, totAbundance);
  }

  return {
    isTruncated,
    compositionGroups,
    groupId2GroupIndex, // converts a group identifier (as specified for a OTU) to the index in the compositionGroups array
    columnsGroupAbundances, // [colIndex][groupIndex] => abundance per column per group
    columnsGroupAbundancesSelected, // [colIndex][groupIndex] => abundance per column per group for currently selected rows
    columnTotalAbundances, // [colIndex] => total abundance for a sample, over all groups
    maxColumnTotalAbundance,
    plotRelative,
  };
}

function createAbundanceSortIndexes(compositionGroupInfo: any, referenceGroupNr: number) {
  let colAbundances = compositionGroupInfo.columnTotalAbundances;
  if (referenceGroupNr >= 0) {
    // reference group chosen
    if (!compositionGroupInfo.plotRelative) {
      colAbundances = compositionGroupInfo.columnsGroupAbundances.map((column: number[]) => column[referenceGroupNr]);
    } else {
      // no reference group chosen
      colAbundances = compositionGroupInfo.columnsGroupAbundances.map((column: number[], idx: number) =>
        compositionGroupInfo.columnTotalAbundances[idx] > 0
          ? column[referenceGroupNr] / compositionGroupInfo.columnTotalAbundances[idx]
          : 0,
      );
    }
  }

  const colCount = colAbundances.length;
  const colSortIndex = getSortIndex(colAbundances, true);

  const colSortInvIndex: number[] = [];
  for (let nr = 0; nr < colCount; nr++) colSortInvIndex.push(0);
  for (let nr = 0; nr < colCount; nr++) colSortInvIndex[colSortIndex[nr]] = nr;

  return [colSortIndex, colSortInvIndex]; // returns array with sort index & inverted sort index
}

/**
 * Orders the composition groups based on an optional custom color order defined
 * in the global dashboard config or chart config.
 * This ensures that the color legend and the plot use the same group order.
 */
function sortGroupsByColor(
  compositionGroups: TpCompositionGroupInfo[],
  boundData: TpBoundData,
  config: TpChartConfigParams,
  globalColorMappingConfig: TpGlobalChartColorMappingConfig | undefined,
) {
  const columnInfo = boundData.bindings.compositionGroups.columnInfo;
  const customGlobalColorOrder = getCategoricalCustomGlobalConfigOrder(globalColorMappingConfig, boundData, columnInfo);
  const customConfigColorOrder = getCategoricalCustomConfigOrder(config, 'compositionGroups', columnInfo);
  const customColorOrder = customConfigColorOrder ?? customGlobalColorOrder;

  // If no custom color order is defined, we keep the original order (which is based on abundance)
  if (!customColorOrder) return compositionGroups;

  // Convert names to category IDs.
  // Convert to string as well as here the groupIds are strings due to the use of Object.keys in the code.
  const orderGroupIds = customColorOrder
    .map((groupName) => {
      return columnInfo.catConvertorInv?.[groupName]?.toString() ?? null;
    })
    .filter((groupId) => groupId != null);

  // Sort a copy of compositionGroups based on the order of groupIds in orderGroupIds.
  // Groups that are not in orderGroupIds are placed at the end, in their original order.
  const compositionGroupsSorted = [...compositionGroups].sort((group1, group2) => {
    const group1Order = orderGroupIds.indexOf(group1.groupId);
    const group2Order = orderGroupIds.indexOf(group2.groupId);
    if (group1Order === -1 && group2Order === -1) return 0;
    if (group1Order === -1) return 1;
    if (group2Order === -1) return -1;
    return group1Order - group2Order;
  });
  return compositionGroupsSorted;
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);

  const dataTable: TpDataTable = boundData.dataTable;
  const columnCanonicalTypeInfo = boundData.matrixColumnInfo!.columnCanonicalTypeInfo;
  const matrixColumns = boundData.matrixColumnInfo!.matrixColumns;
  const dataTableColumns = boundData.matrixColumnInfo!.dataTableColumns;
  const dataHoverLabelcolumnInfo = boundData.bindings.hoverLabelColumn.columnInfo;
  const dataHoverLabelcolumn = boundData.bindings.hoverLabelColumn.data;
  const matrixColumnsData = boundData.matrixColumnInfo!.matrixColumnsData;
  const rowCompositionGroupColInfo = boundData.bindings.compositionGroups.columnInfo;
  const getItemIndexById = boundData.getItemIndexById;

  const title = generateChartTitle(
    dataContext,
    dataBinding,
    config,
    `${rowCompositionGroupColInfo.name} vs. ${columnCanonicalTypeInfo.name}`,
  );

  const dataColItemIds =
    boundData.matrixColumnInfo!.dataTableColumns.columnData[boundData.matrixColumnInfo!.dataTableColumns.colItemId];

  const dataColumnLabels = convertColumnDataListToDisplayString(
    boundData.bindings.columnLabelId.columnInfo,
    boundData.bindings.columnLabelId.data,
  );

  const colCount = matrixColumns.length;

  const compositionGroupInfo = calculateCompositionGroups(boundData, config);

  let colSortIndex: any;
  let colSortInvIndex: any;

  // Figure out if a reference group was defined (e.g. for sorting)
  let referenceGroupNr = -1;
  const referenceGroupName = config.referenceGroupName;
  if (referenceGroupName) {
    for (const [index, group] of compositionGroupInfo.compositionGroups.entries())
      if (group.groupName == referenceGroupName) referenceGroupNr = index;
    if (referenceGroupNr < 0)
      throw getUserError(`Selected reference group is not part of displayed list: ${referenceGroupName}`);
  }

  if (boundData.bindings.sortColumnBy) {
    // sort columns by a column property
    [colSortIndex, colSortInvIndex] = createSortIndexes(
      colCount,
      false,
      boundData.bindings.sortColumnBy?.columnInfo,
      boundData.bindings.sortColumnBy?.data,
    );
  } else {
    // sort columns by abundance
    [colSortIndex, colSortInvIndex] = createAbundanceSortIndexes(compositionGroupInfo, referenceGroupNr);
  }

  const hasColumnsDendrogram = isDendrogramBinding(dataBinding.aspectBindings.sortColumnBy?.columnId);

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, dataColumnLabels, colSortIndex);
  }

  const colorSchemeCategorical = config['colorsScheme'];
  const colors = getDiscreteColorSet(colorSchemeCategorical).colors;

  // Define color and color legend sort order
  const colorOrderedCompositionGroups = sortGroupsByColor(
    compositionGroupInfo.compositionGroups,
    boundData,
    config,
    widgetGlobalConfig.colorMapping,
  );

  // Create colors for each group
  for (let groupNr = 0; groupNr < colorOrderedCompositionGroups.length; groupNr++) {
    const groupInfo = colorOrderedCompositionGroups[groupNr];
    if (!groupInfo.isOtherGroup) {
      if (groupNr < colors.length) groupInfo.color = colors[groupNr];
      else groupInfo.color = getThemedGrayFraction(groupNr % 2 == 0 ? 0.55 : 0.65);
    } else {
      groupInfo.color = getThemedGrayFraction(0.8);
    }
  }

  function renderStacks(renderContext: TpAreaRenderContext) {
    const { mapx, mapy } = renderContext;
    const ctx = renderContext.canvasContext;

    renderGrid(renderContext, false, true);

    // Iterate over the columns = samples
    for (let i = 0; i < matrixColumns.length; i++) {
      const colNr = colSortIndex[i];
      const groupAbundances = compositionGroupInfo.columnsGroupAbundances[colNr];
      const groupAbundancesSelected = compositionGroupInfo.columnsGroupAbundancesSelected[colNr];
      const x1 = mapx((i + 0.1) / colCount);
      const x2 = mapx((i + 0.9) / colCount);

      if (hasColumnsDendrogram) {
        // draw guiding line connecting stacked bar to dendrogram leaf
        ctx.fillStyle = getThemedGrayFraction(0.8).toCanvasString();
        ctx.fillRect((x1 + x2) / 2, renderContext.areaYMin, 1, renderContext.areaYMax - renderContext.areaYMin);
      }

      const totAbundance = compositionGroupInfo.columnTotalAbundances[colNr];
      if (totAbundance > 0) {
        let relativeFactor = 1;
        if (compositionGroupInfo.plotRelative) relativeFactor = 1 / compositionGroupInfo.columnTotalAbundances[colNr];
        let stackedAbundance = 0;
        for (let groupNr = 0; groupNr < compositionGroupInfo.compositionGroups.length; groupNr++) {
          // iterate over compositiong groups
          const groupInfo = compositionGroupInfo.compositionGroups[groupNr];
          ctx.fillStyle = groupInfo.color.toCanvasString();
          const y1 = mapy(relativeFactor * stackedAbundance);
          const y2 = mapy(relativeFactor * (stackedAbundance + groupAbundancesSelected[groupNr]));
          ctx.fillRect(x1, y2, x2 - x1, y1 - y2);
          if (groupAbundancesSelected[groupNr] < groupAbundances[groupNr]) {
            ctx.fillStyle = groupInfo.color.lighten(0.8).toCanvasString();
            const y1 = mapy(relativeFactor * (stackedAbundance + groupAbundancesSelected[groupNr]));
            const y2 = mapy(relativeFactor * (stackedAbundance + groupAbundances[groupNr]));
            ctx.fillRect(x1, y2, x2 - x1, y1 - y2);
          }
          stackedAbundance += groupAbundances[groupNr];
        }
      }
    }
  }

  function renderColumnDendrogram(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv, mapinvx, mapinvy, areaYMin, areaYMax, pixelInvConv } = renderContext;
    const ctx = renderContext.canvasContext;
    const sortColumnsFieldId = dataBinding.aspectBindings.sortColumnBy?.columnId;
    const dendrogram = getDendrogram(dataContext, getDendrogramBinding(sortColumnsFieldId));
    const fc = 0.95 / dendrogram.tree.distance;
    ctx.strokeStyle = currentTheme.colors.text.toCanvasString();
    function renderTree(node: any) {
      if (!node.itemId) {
        renderTree(node.leaf1);
        renderTree(node.leaf2);
        ctx.beginPath();
        ctx.moveTo(mapx(node.leaf1.index / colCount), mapy(node.leaf1.distance * fc) - pixelConv(2));
        ctx.lineTo(mapx(node.leaf1.index / colCount), mapy(node.distance * fc) - pixelConv(2));
        ctx.lineTo(mapx(node.leaf2.index / colCount), mapy(node.distance * fc) - pixelConv(2));
        ctx.lineTo(mapx(node.leaf2.index / colCount), mapy(node.leaf2.distance * fc) - pixelConv(2));
        ctx.stroke();
      }
    }
    renderTree(dendrogram.tree);
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    legendItems.push({
      type: LEGEND_ITEM_TYPES.NamedColorSet,
      items: colorOrderedCompositionGroups.map((group) => ({
        name: group.groupName,
        color: group.color,
      })),
    });
    renderLegendItems(renderContext, legendItems);
  }

  const legendBottomSizeHeight = chartDims.legendHeight;

  const titleSize = chartDims.headerHeight;
  const columnLabelSize = 80;

  const columnDendrogramSize = hasColumnsDendrogram ? 40 : 0;

  const padding = chartDims.padding;

  let minVal = 0;
  let maxVal = 1;
  if (!compositionGroupInfo.plotRelative) {
    minVal = -0.01 * compositionGroupInfo.maxColumnTotalAbundance;
    maxVal = 1.05 * compositionGroupInfo.maxColumnTotalAbundance;
  }

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(minVal, maxVal);
  const zpRenderRangeX = createRenderRange(RenderMinDiffAbs(padding + chartDims.yAxisWidth), RenderMaxDiffAbs(padding));
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + titleSize + columnDendrogramSize),
    RenderMaxDiffAbs(padding + columnLabelSize + legendBottomSizeHeight),
  );

  const areaStacks: TpChartAreaDefinition = {
    id: 'stacks',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    render: renderStacks,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    },

    {
      id: 'title',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },

    areaStacks,
    {
      id: 'columnLabels',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + columnLabelSize + legendBottomSizeHeight),
        RenderMaxDiffAbs(padding + legendBottomSizeHeight),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
  ];

  if (hasColumnsDendrogram)
    chartAreas.push({
      id: 'columnDendrogram',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + titleSize),
        RenderMinDiffAbs(padding + titleSize + columnDendrogramSize),
      ),
      zoomPanX: true,
      render: renderColumnDendrogram,
    });

  chartAreas.push({
    id: 'legend',
    renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
    renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSizeHeight), RenderMaxDiffAbs(padding)),
    render: renderLegend,
  });

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
    columnCanonicalTypeInfo,
    matrixColumns,
    dataTableColumns,
  };

  return {
    definition,
    areaStacks,
    dataTable,
    dataColumnLabels,
    dataHoverLabelcolumnInfo,
    dataHoverLabelcolumn,
    columnCanonicalTypeInfo,
    matrixColumns,
    colSortIndex,
    colSortInvIndex,
    getItemIndexById,
    matrixColumnsData,
    rowCompositionGroupColInfo,
    compositionGroupInfo,
    plotRelative: compositionGroupInfo.plotRelative,
    dataColItemIds,
    title,
  };
}
